import React, { useState } from "react";
import { Box, Typography, useTheme, useMediaQuery, IconButton, Button } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { motion, AnimatePresence } from "motion/react";
import styled from "styled-components";
import { useThemeMode } from "@/colors";

const ShowcaseContainer = styled(Box)`
  padding: 32px 24px;
  background: ${props => props.theme.palette.background.default};
  text-align: center;
  justify-content: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentWrapper = styled(Box)`
  max-width: 1400px;
  margin: 0 auto;
`;

const CarouselContainer = styled(Box)`
  .MuiPaper-root {
    background: transparent !important;
    box-shadow: none !important;
  }
`;

const CarouselWrapper = styled(Box)`
  position: relative;
  overflow: hidden;
  border-radius: 16px;
`;

const NavigationButton = styled(IconButton)`
  position: absolute;
  top: 50%;
  // transform: translateY(-50%);
  z-index: 10;
  background: ${props => props.theme.palette.background.paper} !important;
  color: ${props => props.theme.palette.text.primary} !important;
  width: 48px;
  height: 48px;
  border: 1px solid ${props => props.theme.palette.divider};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.palette.action.hover} !important;
    transform: scale(1.1);
    box-shadow: ${props => props.theme.shadows[3]};
  }
`;

const MapsGrid = styled(motion.div)`
  padding-top: 16px;
  padding-bottom: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 32px;
  max-width: 1200px;
  margin: 0 auto;
  @media screen and (max-width: 1010px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`;

const MapImage = styled.img`
  width: 300px;
  height: 300px;
  object-fit: cover;
  display: block;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  &:hover {
    animation: hover-animation 1s;
    box-shadow: 0 12px 25px rgba(0, 0, 0, 0.2);
  }
  @keyframes hover-animation {
    from {
      transform: translateY(0%);
    }
    to {
      transform: translateY(-8px);
    }
  },
`;

const DotsContainer = styled(Box)`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 32px;
`;

const Dot = styled(Box)<{ active: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.active ? props.theme.palette.primary.main : props.theme.palette.divider};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.palette.primary.main};
    transform: scale(1.2);
  }
`;

// Mock map data - in a real app, this would come from an API
const mapSets = [
  [
    {
      id: 1,
      title: "SAN FRANCISCO",
      subtitle: "California, USA",
      image: "/mexico-city.png", // Using the existing SF image
    },
    {
      id: 2,
      title: "NDOLA",
      subtitle: "Zambia",
      image: "/cape-town.png", // Placeholder - would be different in real app
    },
    {
      id: 3,
      title: "MADRID",
      subtitle: "Spain",
      image: "/white-sample.png", // Placeholder - would be different in real app
    }
  ],
  [
    {
      id: 4,
      title: "TOKYO",
      subtitle: "Japan",
      image: "/san-fran.png", // Placeholder
    },
    {
      id: 5,
      title: "LONDON",
      subtitle: "United Kingdom",
      image: "/madrid.png", // Placeholder
    },
    {
      id: 6,
      title: "NEW YORK",
      subtitle: "New York, USA",
      image: "/pink-sample.png", // Placeholder
    }
  ],
  [
    {
      id: 7,
      title: "PARIS",
      subtitle: "France",
      image: "/boulder.png", // Placeholder
    },
    {
      id: 8,
      title: "SYDNEY",
      subtitle: "Australia",
      image: "/mumbai.png", // Placeholder
    },
    {
      id: 9,
      title: "BERLIN",
      subtitle: "Germany",
      image: "/nz.png", // Placeholder
    }
  ]
];

export const MapsShowcase: React.FC = () => {
  const theme = useTheme();
  const { colors } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % mapSets.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + mapSets.length) % mapSets.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.6,
        ease: "easeOut"
      }
    })
  };

  return (
    <ShowcaseContainer>
      <ContentWrapper>
        <CarouselContainer>
          <CarouselWrapper>
            <AnimatePresence mode="wait" custom={currentIndex}>
              <MapsGrid
                key={currentIndex}
                custom={currentIndex}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
              >
                {mapSets[currentIndex].map((map, index) => (
                  <>
                  {/* // <MapCard
                  //   key={map.id}
                  //   custom={index}
                  //   variants={cardVariants}
                  //   initial="hidden"
                  //   animate="visible"
                  //   whileHover={{ 
                  //     scale: 1.05,
                  //     transition: { duration: 0.3 }
                  //   }}
                  // > */}
                    <MapImage 
                      src={map.image} 
                      alt={`Map of ${map.title}`}
                      style={{
                        // filter: `hue-rotate(${currentIndex * 60}deg) saturate(0.8)`
                      }}
                    />
                    {/* <MapOverlay>
                      <MapTitle>{map.title}</MapTitle>
                      <MapSubtitle>{map.subtitle}</MapSubtitle>
                    </MapOverlay>
                  </MapCard> */}
                  </>
                ))}
              </MapsGrid>
            </AnimatePresence>
            
            {!isMobile && (
              <>
                <NavigationButton 
                  className="left" 
                  onClick={prevSlide}
                  aria-label="Previous maps"
                >
                  <ChevronLeft />
                </NavigationButton>
                <NavigationButton 
                  className="right" 
                  onClick={nextSlide}
                  aria-label="Next maps"
                >
                  <ChevronRight />
                </NavigationButton>
              </>
            )}
          </CarouselWrapper>
          
          <DotsContainer>
            {mapSets.map((_, index) => (
              <Dot
                key={index}
                active={index === currentIndex}
                onClick={() => goToSlide(index)}
              />
            ))}
          </DotsContainer>
        </CarouselContainer>
      </ContentWrapper>
    </ShowcaseContainer>
  );
};
